import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { FormControlOption } from '@app/forms/subscription/definition/interface/formControlOption.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Big } from 'big.js';
import { Observable, catchError, finalize, map, take, tap } from 'rxjs';
import { SubscriptionFacadeService } from '../../../services/subscription-facade.service';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';

@UntilDestroy()
@Component({
  selector: 'scpi-configuration',
  templateUrl: './scpi-configuration.component.html',
  styleUrls: ['./scpi-configuration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SCPIConfigurationComponent
  extends EaSubscriptionBuilder
  implements OnInit, AfterViewInit
{
  obser$: Observable<any>;
  options: FormControlOption[] = [
    { value: 'A', label: 'Le montant à investir', suffix: { title: 'A' } },
    { value: 'B', label: 'Le nombre de parts', suffix: { title: 'B' } },
  ];
  option2: FormControlOption[] = [
    { value: 'A', label: '' },
    { value: 'B', label: '' },
  ];
  dismemberment: boolean;
  scpiOwnershipType: string;

  // dismembermentDurationDefaultValue: number = 5;

  constructor(
    protected formConstructor: PrFormConstructor,

    private referenceDao: ReferenceDao,
    private subscriptionFacade: SubscriptionFacadeService,
    private cdr: ChangeDetectorRef,
  ) {
    super(formConstructor);
  }
  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    const pledge = this.subscriptionFacade.getPledge();
    this.initForm(null, {
      pledgeParameters: pledge.parameters || {},
    });

    const products = pledge?.products;
    const productIds = products?.map(product => product?.productId);

    this.scpiOwnershipType = pledge.parameters.scpiOwnershipType;
    this.dismemberment =
      this.scpiOwnershipType == 'USUFRUIT' ||
      this.scpiOwnershipType == 'NU_PROPRIO';
    this.loading = true;
    this.obser$ = this.referenceDao.getScpi().pipe(
      take(1),
      catchError(err => {
        this.loading = false;
        throw err;
      }),

      map(scpiList => scpiList.filter(scpi => productIds.includes(scpi.id))),
      tap(scpiList => {
        this.loading = false;
        const scpiProductWithPrice = this.initScpiList(scpiList);

        this.initForm(scpiProductWithPrice, {
          pledgeParameters: pledge.parameters || {},
        });
        this.scpiFormValueChanges();
        this.watchInvestmentDurationViager();
        this.refreshDerivedAmounts();
      }),

      finalize(() => {
        this.cdr.detectChanges();
      }),
    );
  }
  private initScpiList(scpiList: any) {
    const pledgeProducts = this.subscriptionFacade.getPledge().products;

    return scpiList.map(scpi => {
      const scpiProductPrice = pledgeProducts.find(
        pledgeproduct => pledgeproduct.productId == scpi.id,
      );

      if (scpiProductPrice) {
        const {
          scpiNbPart,
          scpiPartPrice,
          scpiInvestmentPrice,
          dismembermentDuration,
          dismembermentDistributionKey,
          scpiDismembermentPartPrice,
          dismembermentFullPropertyAmount,
          scpiPartDivisible,
        } = scpiProductPrice?.parameters || {};

        return {
          ...scpi,
          scpiNbPart,
          scpiPartPrice,
          scpiInvestmentPrice,
          scpiOwnershipType: this.scpiOwnershipType,
          dismembermentDuration,
          dismembermentDistributionKey,
          scpiDismembermentPartPrice,
          dismembermentFullPropertyAmount,
          scpiPartDivisible,
        };
      }

      return scpi;
    });
  }
  /** Durée de démembrement imposée par une souscription en viager. */
  private static readonly VIAGER_DISMEMBERMENT_DURATION = 99;

  /** Souscription en viager : paramètre de la souscription, pas du produit. */
  get investmentDurationViager(): boolean {
    return !!this.form?.get('parameters')?.get('investmentDurationViager')
      ?.value;
  }

  /**
   * Le viager fixe la durée de démembrement de toutes les SCPI de la slide et
   * masque la saisie. Appliqué aussi à l'ouverture, une souscription rouverte
   * en viager pouvant porter une durée saisie avant que la question existe.
   */
  private watchInvestmentDurationViager() {
    const control = this.form?.get('parameters')?.get('investmentDurationViager');
    if (!control) return;

    if (control.value) this.applyViagerDismembermentDuration();

    control.valueChanges.pipe(untilDestroyed(this)).subscribe(viager => {
      if (viager) this.applyViagerDismembermentDuration();
      this.cdr.markForCheck();
    });
  }

  private applyViagerDismembermentDuration() {
    this.scpis.controls.forEach(scpi =>
      scpi
        ?.get('parameters')
        ?.get('dismembermentDuration')
        ?.setValue(SCPIConfigurationComponent.VIAGER_DISMEMBERMENT_DURATION),
    );
  }

  private scpiFormValueChanges() {
    this.scpis.controls.forEach(scpi => {
      scpi
        ?.get('parameters')
        ?.get('dismembermentDuration')
        .valueChanges.pipe(untilDestroyed(this))
        .subscribe(value => {
          if (value) {
            this.updateDismembermentDistributionKey(scpi, value);
          }
        });

      scpi
        ?.get('parameters')
        ?.get('dismembermentDistributionKey')
        .valueChanges.pipe(untilDestroyed(this))
        .subscribe(value => {
          this.updateScpiDismembermentPartPrice(scpi, value);
          this.updateScpiInvestmentPrice(scpi);
          this.updateDismembermentFullPropertyAmount(scpi);
        });

      scpi
        ?.get('parameters')
        ?.get('slightlyAmount')
        .valueChanges.pipe(untilDestroyed(this))
        .subscribe(value => {
          if (value) {
            this.updateScpiNbPart(scpi, value);
          }
        });

      scpi
        .get('parameters')
        .get('scpiNbPart')
        .valueChanges.pipe(untilDestroyed(this))
        .subscribe(value => {
          if (value && scpi?.get('parameters')?.get('scpiPartPrice').value) {
            this.updateScpiInvestmentPrice(scpi);
            this.updateDismembermentFullPropertyAmount(scpi);
          }
        });
    });
  }

  private updateDismembermentDistributionKey(
    scpi: AbstractControl,
    value: any,
  ) {
    let keyArray: any[] = [];
    if (this.scpiOwnershipType == 'USUFRUIT') {
      keyArray = scpi?.get('parameters')?.get('usufructKeys')?.value;
    } else if (this.scpiOwnershipType == 'NU_PROPRIO') {
      keyArray = scpi?.get('parameters')?.get('bareOwnershipKeys')?.value;
    }

    let filtered: any[] = keyArray.filter(
      yearkeyPair => yearkeyPair['year'] === value,
    );
    if (filtered.length > 0 && filtered[0]['value']) {
      scpi
        ?.get('parameters')
        ?.get('dismembermentDistributionKey')
        .setValue(
          this.format(this.toBigOrZero(filtered[0]['value']).times(100)),
        );
    } else {
      scpi?.get('parameters')?.get('dismembermentDistributionKey').setValue(0);
    }
  }

  private updateScpiDismembermentPartPrice(scpi: AbstractControl, value: any) {
    const partPrice = scpi?.get('parameters')?.get('scpiPartPrice').value;

    scpi
      ?.get('parameters')
      ?.get('scpiDismembermentPartPrice')
      .setValue(
        this.format(
          this.toBigOrZero(value).div(100).times(this.toBigOrZero(partPrice)),
        ),
      );
  }

  // appeler en pleine pro aussi
  private updateScpiInvestmentPrice(scpi: AbstractControl) {
    const partPrice = scpi?.get('parameters')?.get('scpiPartPrice').value;
    const nbPart = scpi?.get('parameters')?.get('scpiNbPart').value;

    if (this.dismemberment) {
      const key = scpi
        ?.get('parameters')
        ?.get('dismembermentDistributionKey').value;
      const investmentPrice = this.toBigOrZero(key)
        .div(100)
        .times(this.toBigOrZero(partPrice))
        .times(this.toBigOrZero(nbPart));
      scpi
        ?.get('parameters')
        ?.get('scpiInvestmentPrice')
        .setValue(this.format(investmentPrice));
    } else {
      scpi
        ?.get('parameters')
        ?.get('scpiInvestmentPrice')
        .setValue(
          this.format(
            this.toBigOrZero(partPrice).times(this.toBigOrZero(nbPart)),
          ),
        );
    }
  }

  private updateDismembermentFullPropertyAmount(scpi: AbstractControl) {
    const nbPart = scpi?.get('parameters')?.get('scpiNbPart').value;
    const partPrice = scpi?.get('parameters')?.get('scpiPartPrice').value;
    const fullPropertyAmount = this.toBigOrZero(nbPart).times(
      this.toBigOrZero(partPrice),
    );
    scpi
      ?.get('parameters')
      ?.get('dismembermentFullPropertyAmount')
      .setValue(this.format(fullPropertyAmount));
  }

  /**
   * Recalcule le prix de part démembré et les montants investis à partir de la
   * clé et du prix de part : une souscription rouverte porte des valeurs
   * enregistrées avec une précision inférieure.
   */
  private refreshDerivedAmounts() {
    this.scpis.controls.forEach(scpi => {
      const parameters = scpi?.get('parameters');
      const key = parameters?.get('dismembermentDistributionKey').value;
      if (this.dismemberment && key) {
        this.updateScpiDismembermentPartPrice(scpi, key);
      }
      if (
        parameters?.get('scpiNbPart').value &&
        parameters?.get('scpiPartPrice').value
      ) {
        this.updateScpiInvestmentPrice(scpi);
        this.updateDismembermentFullPropertyAmount(scpi);
      }
    });
  }

  private updateScpiNbPart(scpi: AbstractControl, value: any) {
    scpi?.get('parameters')?.get('scpiNbPart').setValue(value);
  }

  /** Nombre de décimales d'un nombre de parts selon que la part est fractionnable. */
  private partDecimalPlaces(scpiPartDivisible: boolean): number {
    return scpiPartDivisible ? this.decimalPlaces : 0;
  }

  /**
   * Nombre de parts formaté, vide tant que le montant souhaité ou le prix de
   * part ne permettent pas de le calculer : l'écran n'affiche alors aucune
   * option de souscription.
   */
  private formatParts(nbPart: number, scpiPartDivisible: boolean): string {
    return Number.isNaN(nbPart)
      ? ''
      : this.format(nbPart, this.partDecimalPlaces(scpiPartDivisible));
  }

  calculateSCPIParts(
    scpiPartDivisible: boolean,
    valeursSouhaite: number,
    dismemberment: boolean,
    partPrice: number,
    partPriceDismemberment: number,
    isSup: boolean,
  ): [number, number] {
    const prixPart = this.toBigOrZero(
      dismemberment ? partPriceDismemberment : partPrice,
    );
    const montant = this.toBig(valeursSouhaite);
    if (prixPart.eq(0) || montant === null || montant.eq(0)) {
      return [NaN, NaN];
    }

    const decimales = this.partDecimalPlaces(scpiPartDivisible);
    const smallestPart = new Big(10).pow(-decimales);
    let nbPart = montant.div(prixPart).round(decimales, Big.roundDown);
    if (isSup) {
      nbPart = nbPart.plus(smallestPart);
    }

    const prix = nbPart.times(prixPart);
    return [Number(this.format(prix)), Number(this.format(nbPart, decimales))];
  }

  formatSCPIPriceWithPartsLower(
    scpiPartDivisible: boolean,
    valeursSouhaite: number,
    dismemberment: boolean,
    partPrice: number,
    partPriceDismemberment: number,
  ): string {
    const [prixInf, nbPartInf] = this.calculateSCPIParts(
      scpiPartDivisible,
      valeursSouhaite,
      dismemberment,
      partPrice,
      partPriceDismemberment,
      false,
    );

    if (Number.isNaN(nbPartInf)) {
      return '';
    }
    return `${this.format(prixInf)} € (soit ${this.formatParts(
      nbPartInf,
      scpiPartDivisible,
    )} parts)`;
  }

  formatSCPIPriceWithPartsSuperior(
    scpiPartDivisible: boolean,
    valeursSouhaite: number,
    dismemberment: boolean,
    partPrice: number,
    partPriceDismemberment: number,
  ): string {
    const [prixSup, nbPartSup] = this.calculateSCPIParts(
      scpiPartDivisible,
      valeursSouhaite,
      dismemberment,
      partPrice,
      partPriceDismemberment,
      true,
    );

    if (Number.isNaN(nbPartSup)) {
      return '';
    }
    return `${this.format(prixSup)} € (soit ${this.formatParts(
      nbPartSup,
      scpiPartDivisible,
    )} parts)`;
  }

  formatSCPIPartsLower(
    scpiPartDivisible: boolean,
    valeursSouhaite: number,
    dismemberment: boolean,
    partPrice: number,
    partPriceDismemberment: number,
  ): string {
    const [, nbPartInf] = this.calculateSCPIParts(
      scpiPartDivisible,
      valeursSouhaite,
      dismemberment,
      partPrice,
      partPriceDismemberment,
      false,
    );

    return this.formatParts(nbPartInf, scpiPartDivisible);
  }

  formatSCPIPartsSuperior(
    scpiPartDivisible: boolean,
    valeursSouhaite: number,
    dismemberment: boolean,
    partPrice: number,
    partPriceDismemberment: number,
  ): string {
    const [, nbPartSup] = this.calculateSCPIParts(
      scpiPartDivisible,
      valeursSouhaite,
      dismemberment,
      partPrice,
      partPriceDismemberment,
      true,
    );

    return this.formatParts(nbPartSup, scpiPartDivisible);
  }

  totalInvestment(
    partPrice: number,
    partPriceDismemberment: number,
    nbPart: number,
    dismemberment: boolean,
  ) {
    const prixPart = this.toBigOrZero(
      dismemberment ? partPriceDismemberment : partPrice,
    );
    return this.format(prixPart.times(this.toBigOrZero(nbPart)));
  }

  onSubmit() {
    this.form.markAsDirty();
    this.subscriptionFacade.selectErrorInputAndScroll(this);

    if (this.form.valid) {
      this.subscriptionFacade.submitPledge(this, 'products');
    }
  }
  prev() {
    this.subscriptionFacade.previousStep();
  }
}
